import { noop } from 'lodash';
import { useMemo } from 'react';

import { Patient, PatientEntitlement, PatientEntitlementQuery } from '@dreryk/egabinet-patient-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { patientEntitlementsTranslationKeys } from '../../translations';
import { OnPatientEntitlementDocumentUpdated } from '../update/PatientEntitlementUpdateForm.types';
import { PatientEntitlementUpdateIcon } from '../update/PatientEntitlementUpdateIcon';

import { LocalDateCell } from './LocalDateCell';
import { PatientEntitlementTypeCell } from './PatientEntitlementTypeCell';

const DEFAULT_ORDERINGS: Ordering<PatientEntitlement>[] = [
   {
      descending: false,
      property: 'patientEntitlementTypeDisplayName',
   },
];

type PatientEntitlementQueryParameters = {
   patientId: Patient['id'];
};

export const usePatientEntitlementDataConfiguration = (
   parameters: PatientEntitlementQueryParameters
): LazyLoadedDataConfigurationByCqrsQuery<PatientEntitlement> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PatientEntitlement>(() => {
      const query = new PatientEntitlementQuery();
      query.patientId = parameters.patientId;
      return query;
   }, DEFAULT_ORDERINGS);
};

type PatientEntitlementsTableColumnsOptions = {
   patientId: Patient['id'];
   onPatientEntitlementDocumentUpdated: OnPatientEntitlementDocumentUpdated;
};

export const usePatientEntitlementsTableColumns = (
   options: PatientEntitlementsTableColumnsOptions
): TableColumnConfiguration<PatientEntitlement>[] => {
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         patientEntitlementTypeDisplayName: '50%',
         entitlementConfirmationTypeName: '50%',
         confirmingDocumentNumber: '200px',
         validFrom: '240px',
         validTo: '240px',
         options: '40px',
      });

      return [
         {
            name: 'patientEntitlementTypeDisplayName',
            accessor: noop,
            label: patientEntitlementsTranslationKeys.category,
            width: columnsWidth.patientEntitlementTypeDisplayName,
            renderer: PatientEntitlementTypeCell,
         },
         {
            name: 'entitlementConfirmationTypeName',
            accessor: (patientEntitlement) => patientEntitlement.entitlementConfirmationTypeName,
            label: patientEntitlementsTranslationKeys.documentKind,
            width: columnsWidth.entitlementConfirmationTypeName,
         },
         {
            name: 'confirmingDocumentNumber',
            accessor: (patientEntitlement) => patientEntitlement.confirmingDocumentNumber,
            label: patientEntitlementsTranslationKeys.confirmingDocumentNumber,
            width: columnsWidth.confirmingDocumentNumber,
         },
         {
            name: 'validFrom',
            accessor: ({ validFrom }) => validFrom,
            label: patientEntitlementsTranslationKeys.issueDate,
            width: columnsWidth.validFrom,

            renderer: ({ bean }: ValueRendererProps<PatientEntitlement, PatientEntitlement>) =>
               bean.validFrom ? <LocalDateCell value={bean.validFrom} /> : null,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'validTo',
            accessor: ({ validTo }) => validTo,
            label: patientEntitlementsTranslationKeys.validityDate,
            width: columnsWidth.validTo,

            renderer: ({ bean }: ValueRendererProps<PatientEntitlement, PatientEntitlement>) =>
               bean.validTo ? <LocalDateCell value={bean.validTo} /> : null,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,

            renderer: (props: ValueRendererProps<PatientEntitlement>) => (
               <PatientEntitlementUpdateIcon
                  patientEntitlement={props.bean}
                  patientId={options.patientId}
                  onPatientEntitlementDocumentUpdated={options.onPatientEntitlementDocumentUpdated}
               />
            ),
            sortingDisabled: true,
         },
      ];
   }, [options.onPatientEntitlementDocumentUpdated, options.patientId]);
};
